import { UserProfile, AuthStatus, ExperimentEntryResponse, StaffRole } from '../types';

export class AuthService {
  private static instance: AuthService;
  private authStatus: AuthStatus | null = null;

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * 認証ステータスを取得
   */
  async getAuthStatus(options?: { forceRefresh?: boolean }): Promise<AuthStatus> {
    if (this.authStatus && !options?.forceRefresh) {
      return this.authStatus;
    }

    try {
      const response = await fetch('/api/auth/status', {
        credentials: 'include'
      });

      if (!response.ok) {
        this.authStatus = null;
        throw new Error('認証ステータス取得失敗');
      }

      this.authStatus = await response.json();
      return this.authStatus!;
    } catch (error) {
      console.error('認証ステータス取得エラー:', error);
      // デフォルト値を返す
      this.authStatus = {
        authRequired: false,
        authMode: 'disabled',
        isAuthenticated: false,
        user: null,
        allowedDomains: [],
        hasEmailRestrictions: false
      };
      return this.authStatus;
    }
  }

  /**
   * ユーザー情報を取得
   */
  async getCurrentUser(): Promise<UserProfile | null> {
    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.authStatus = null;
        }
        return null;
      }

      const data = await response.json();
      return data.user;
    } catch (error) {
      console.error('ユーザー情報取得エラー:', error);
      return null;
    }
  }

  /**
   * Google OAuth認証を開始
   */
  startGoogleAuth(viewRole?: StaffRole): void {
    const query = viewRole ? `?as=${encodeURIComponent(viewRole)}` : '';
    window.location.href = `/api/auth/google${query}`;
  }

  async setStaffViewRole(role: StaffRole): Promise<{ role: StaffRole; accountRole: StaffRole; landingPath: string }> {
    const response = await fetch('/api/auth/view-role', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ role }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || '表示ロールの切り替えに失敗しました。');
    }
    this.resetAuthStatus();
    return data;
  }

  /**
   * 実証実験用ローカル参加者ログイン
   *
   * 学籍番号 (`participantCode`) は必須。サーバー側で空ならエラー文言が返る。
   * `nickname` は省略可。未指定なら学籍番号からサーバー側で自動生成される。
   * `password` は EXPERIMENT_GUEST_PASSWORD が設定されている場合のみ照合される。
   */
  async startExperimentEntry(input: {
    participantCode: string;
    nickname?: string;
    experimentSlug?: string;
    password?: string;
    experimentGroup?: string;
  }): Promise<UserProfile> {
    const data = await this.startExperimentEntryDetailed(input);
    return data.user;
  }

  async startExperimentEntryDetailed(input: {
    participantCode: string;
    nickname?: string;
    experimentSlug?: string;
    password?: string;
    experimentGroup?: string;
  }): Promise<ExperimentEntryResponse> {
    const response = await fetch('/api/auth/experiment-entry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(input),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || '参加者ログインに失敗しました');
    }

    this.resetAuthStatus();
    return data as ExperimentEntryResponse;
  }

  async previewGuestPass(token: string): Promise<{ label: string; expiresAt: string; status: 'active' }> {
    const response = await fetch(`/api/auth/guest-pass/${encodeURIComponent(token)}`, {
      credentials: 'include',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || 'このQRは使えません。');
    }
    return data;
  }

  async startGuestEntry(input: { token: string; displayName?: string }): Promise<UserProfile> {
    const response = await fetch('/api/auth/guest-entry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        token: input.token,
        displayName: input.displayName || undefined,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || 'ゲスト入場に失敗しました。');
    }
    this.resetAuthStatus();
    return data.user as UserProfile;
  }

  /**
   * ログアウト
   */
  async logout(): Promise<void> {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });

      // キャッシュをクリア
      this.authStatus = null;

      // ログインページにリダイレクト
      window.location.href = '/login';
    } catch (error) {
      console.error('ログアウトエラー:', error);
      // エラーでもリダイレクトは実行
      window.location.href = '/login';
    }
  }

  /**
   * 認証が必要かチェック
   */
  async isAuthRequired(): Promise<boolean> {
    const status = await this.getAuthStatus();
    return status.authRequired;
  }

  /**
   * 認証済みかチェック
   */
  async isAuthenticated(): Promise<boolean> {
    const status = await this.getAuthStatus();
    return status.isAuthenticated;
  }

  /**
   * 認証状態をリセット（ページ遷移時など）
   */
  resetAuthStatus(): void {
    this.authStatus = null;
  }
}
